import React, { useEffect, useMemo } from "react";
import "../assets/styles/App.css";
import { Button, Col, Container, Row, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
    Calculator,
    ClipboardCheck,
    ClipboardPen,
    ClipboardList,
    ListRestart,
    ListTodo,
    MapPinned,
    ScanEye,
    UserCircle
} from "lucide-react";
import { connect } from "react-redux";
import { getMyActivities } from "../actions/activityActions";
import cookie from "../utilities/cookie";
import AuthService from "../services/AuthService";
import { useTranslation } from "react-i18next";
import { ISettingState } from "../models/types";
import { getCurrentSubscription, subscribePushMessages } from "../utilities/pushMessaging";
import { savePushSubscription } from "../actions/pushMessageActions";
import * as actionTypes from '../actionsTypes';


interface Props {
    dispatch: Function;
    activityCount?: number;
    setting: ISettingState;
}

const Dashboard: React.FC<Props> = ({ dispatch, activityCount, setting }) => {
    const { i18n } = useTranslation();
    // @ts-ignore
    const t = (txt: string) => i18n.t(txt);
    const navigate = useNavigate();

    const toRequest = () => navigate("/request");
    const toEnquiry = () => navigate("/inquiry");
    const scanInventory = () => navigate("/scan_inventory");
    const handleActivity = () => navigate("/activity");
    const handleOrder = () => navigate("/order");
    const handleLogActivity = () => navigate("/log-activity");
    const handleActuators = () => navigate("/handle-actuators");
    const handleAssign = () => navigate("/assign");
    const handleReturn = () => navigate("/return");
    const handleModify = () => navigate("/modify");
    const handleTrackTrace = () => navigate("/track-trace/locations");

    const permissible = cookie.get("permissible")?.split(",") || [];
    const actionButtons = [
        { name: t("request"), icon: ScanEye, clickHandler: toRequest, enabled: true, visible: permissible.includes("Request") },
        { name: t("inquiry"), icon: ScanEye, clickHandler: toEnquiry, enabled: true, visible: permissible.includes("Inquiry") },
        { name: t("inventory"), icon: Calculator, clickHandler: scanInventory, enabled: true, visible: permissible.includes("Inventory") },
        { name: t("activity.activity"), icon: ClipboardList, clickHandler: handleActivity, enabled: true, notification_count: activityCount, visible: permissible.includes("Activity") },
        { name: t("ordering"), icon: ClipboardList, clickHandler: handleOrder, enabled: true, notification_count: activityCount, visible: permissible.includes("Order") },
        { name: t("log_activity"), icon: ClipboardCheck, clickHandler: handleLogActivity, enabled: true, visible: permissible.includes("Activity") },
        { name: t("actuator_management"), icon: ClipboardCheck, clickHandler: handleActuators, enabled: true, visible: permissible.includes("Actuators") },
        { name: t("asset_assignment.assign"), icon: ListTodo, clickHandler: handleAssign, enabled: true, visible: permissible.includes("Asset Assignment") },
        { name: t("asset_assignment.return"), icon: ListRestart, clickHandler: handleReturn, enabled: true, visible: permissible.includes("Asset Assignment") },
        { name: t("Modify"), icon: ClipboardPen, clickHandler: handleModify, enabled: true, visible: permissible.includes("Modify") },
        { name: t('track_trace.title'), icon: MapPinned, clickHandler: handleTrackTrace, enabled: true, visible: permissible.includes("Track & Trace") },
    ];
    const user = useMemo(() => AuthService.getUser(), []);
    const notificationOptions = user?.options?.notification;
    const wantsPushNotifications = notificationOptions?.receive_sensor_alerts === true || notificationOptions?.new_activity_alert !== false;

    useEffect(() => {
        function handlePushSubscription(subscription: PushSubscription) {
            dispatch({ type: actionTypes.PUSH_SUBSCRIBED, payload: { push_subscribed: 1 } });
            //and refresh
            savePushSubscription(user, subscription)
                .then(() => {
                    console.log('push subscription refreshed on the server')
                })
                .catch(err => console.error(err));
        }

        dispatch(getMyActivities(true));

        if (setting.pushMessaging === true && setting.pushSubscribed !== -1 && wantsPushNotifications) {
            getCurrentSubscription()
                .then(subscription => {
                    if (subscription === null) {
                        subscribePushMessages(user)//also saves...maybe take that out as a side-effect
                            .then(() => console.log('push subscription created and saved on the server'))
                            .catch((err: any) => console.error(err))
                    } else {
                        handlePushSubscription(subscription);
                    }
                })
                .catch(err => console.error(err))
        }
    }, [dispatch, setting.pushMessaging, setting.pushSubscribed, user, wantsPushNotifications]);

    return (
        <Container className="grow py-2">
            <div className="bg-white rounded shadow-md p-1 mb-2 font-bold">
                <UserCircle style={{ marginTop: "-5px", color: "#014cbe" }} />
                <span className="username-text">{user?.name || t("welcome")}!</span>
            </div>
            <div className="bg-white rounded shadow-md p-4 mb-4">
                <h3 className="h5 mb-3">{t("menu_options")}</h3>
                <Row className="g-3">
                    {actionButtons.filter((button) => button.visible).map((button) => (
                        <Col xs={6} key={button.name}>
                            <div className="position-relative">
                                <Button
                                    variant="light"
                                    disabled={!button.enabled}
                                    onClick={button.clickHandler}
                                    className="d-flex flex-column align-items-center justify-content-center py-3 shadow-sm w-100"
                                >
                                    <button.icon className="mb-2 text-primary" size={24} />
                                    <span className="small">{button.name}</span>
                                </Button>
                                {!!button.notification_count && button.notification_count > 0 && (
                                    <Badge bg="info" pill className="position-absolute top-0 start-100 translate-middle">
                                        {button.notification_count}
                                    </Badge>
                                )}
                            </div>
                        </Col>
                    ))}
                </Row>
                {/* {JSON.stringify(setting)} */}
            </div>
        </Container>
    );
};

const mapStateToProps = (state: any) => ({
    activityCount: state.activity.my_activity_count,
    setting: state.setting
});

export default connect(mapStateToProps)(Dashboard);
